import { Box, Button, Center, Flex, Text } from '@chakra-ui/react';

import { Trans } from '@lingui/react/macro';
import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { SortOrder } from '@duke-hq/lib/pagination';
import type { DateRangeKey } from '@duke-hq/svc-api/provider/trpc/routers/tenant/groupRewards/utils';
import { ContributorsLeaderboardTable } from './ContributorsLeaderboardTable';
import { DateRangeFilterButtons } from '../DateRangeFilterButtons';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_ORDER_BY = 'total_spent';

export type SortFilters = {
  orderBy: string | undefined;
  sort: SortOrder | undefined;
  dateRange: DateRangeKey | undefined;
};

function isDateRangeKey(value: unknown): value is DateRangeKey {
  return value === 'currentMonth' || value === 'lastMonth' || value === 'last6Months';
}

function useContributorsBalances(filters: SortFilters, currentLimit: number) {
  const queryResult =
    trpcTenant.groupRewards.listContributorsLeaderboardWithInfiniteScroll.useInfiniteQuery(
      { filters, limit: currentLimit },
      {
        getNextPageParam: (lastPage) => lastPage.nextCursor,
        placeholderData: (prev) => prev,
        staleTime: 5 * 60 * 1000, // 5 minutes
      },
    );

  const balances = useMemo(
    () => queryResult.data?.pages.flatMap((page) => page.items) ?? [],
    [queryResult.data],
  );

  const currentUser = useMemo(() => {
    // Get current user from the first page (it should be the same across all pages)
    const firstPage = queryResult.data?.pages[0];
    return firstPage?.currentUser || null;
  }, [queryResult.data]);

  return {
    ...queryResult,
    balances,
    currentUser,
  };
}

function updateFilters(prevFilters: SortFilters, newFilters: Partial<SortFilters>): SortFilters {
  return {
    ...prevFilters,
    ...newFilters,
    orderBy: newFilters.orderBy ?? prevFilters.orderBy ?? DEFAULT_ORDER_BY,
    sort: newFilters.sort ?? prevFilters.sort ?? SortOrder.DESC,
    dateRange:
      'dateRange' in newFilters
        ? isDateRangeKey(newFilters.dateRange)
          ? newFilters.dateRange
          : undefined
        : prevFilters.dateRange,
  };
}

export function ContributorsLeaderboard() {
  const [filters, setFilters] = useState<SortFilters>({
    orderBy: DEFAULT_ORDER_BY,
    sort: SortOrder.DESC,
    dateRange: 'currentMonth',
  });

  const [currentLimit, setCurrentLimit] = useState(DEFAULT_PAGE_SIZE);
  const maxLoadedItemsRef = useRef(DEFAULT_PAGE_SIZE);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const previousCountRef = useRef(0);

  const { balances, currentUser, fetchNextPage, hasNextPage, isFetchingNextPage, error } =
    useContributorsBalances(filters, currentLimit);
  const maxLoadedItems = Math.max(maxLoadedItemsRef.current, balances.length);
  maxLoadedItemsRef.current = maxLoadedItems;

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (
      balances.length > previousCountRef.current &&
      previousCountRef.current > 0 &&
      tableScrollRef.current
    ) {
      const scrollContainer = tableScrollRef.current;

      // Always scroll to bottom when loading more records
      timeoutId = setTimeout(() => {
        if (scrollContainer != null) {
          scrollContainer.scrollTop = scrollContainer.scrollHeight;
        }
      }, 100);
    }

    previousCountRef.current = balances.length;

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [balances.length]);

  const handleFilterChange = useCallback(
    (newFilters: Partial<SortFilters>) => {
      setCurrentLimit(maxLoadedItems);
      setFilters((prevFilters) => updateFilters(prevFilters, newFilters));
    },
    [maxLoadedItems],
  );

  const handleLoadMore = useCallback(() => {
    setCurrentLimit((prev) => prev + DEFAULT_PAGE_SIZE);
    void fetchNextPage();
  }, [fetchNextPage]);

  if (error) {
    return (
      <Center p={8}>
        <Text color="red.500" textAlign="center">
          <Trans>Error loading data. Please try again.</Trans>
        </Text>
      </Center>
    );
  }

  return (
    <>
      <TableViewLayout
        tableHeight="auto"
        tableBgColor="bg.subtle"
        pageHeader={[
          <Text key="heading" textStyle="2xl" fontWeight="medium">
            <Trans>Top Contributors</Trans>
          </Text>,
        ]}
        filters={
          <Flex w="full" justify="flex-end">
            <DateRangeFilterButtons filters={filters} onFilterChange={handleFilterChange} />
          </Flex>
        }
        table={
          <Box>
            {balances.length > 0 ? (
              <Box>
                <Box ref={tableScrollRef} maxHeight="600px" overflowY="auto" borderRadius="md">
                  <ContributorsLeaderboardTable
                    balances={balances}
                    urlParams={filters}
                    onFilters={handleFilterChange}
                    currentUser={currentUser}
                  />
                </Box>
              </Box>
            ) : (
              <Center p={8}>
                <Text textAlign="center">
                  <Trans>No members found</Trans>
                </Text>
              </Center>
            )}
          </Box>
        }
      />
      {hasNextPage ? (
        <Flex justify="center" my={4}>
          <Button
            onClick={handleLoadMore}
            isLoading={isFetchingNextPage}
            loadingText="Loading more..."
            variant="link"
            size="xs"
            colorScheme="teal"
          >
            <Trans>Load More ({balances.length} loaded)</Trans>
          </Button>
        </Flex>
      ) : null}
    </>
  );
}
